import React, {
  MouseEvent,
  ChangeEvent,
  createContext,
  useRef,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from 'react';
import { Field } from 'formik';
import _isArray from 'lodash/isArray';
import _some from 'lodash/some';
import _every from 'lodash/every';
import _includes from 'lodash/includes';
import _filter from 'lodash/filter';
import _map from 'lodash/map';
import _uniqBy from 'lodash/uniqBy';
import { setFieldValueAndTouched, haveSameContents, helpers } from '@minecraft.utils';
import { SelectOption } from '@minecraft.typings';
import { emptyOption } from '../config';
import { DropdownWithChildrenProviderType, DropdownWithChildrenContextType, DropdownTogglerTypeEnum } from '../types';
import { getSelectionsResult } from '../utils';

const { getAllNestedOptions } = helpers;

const DropdownWithChildrenContext = createContext<DropdownWithChildrenContextType>({
  isDropdownOpen: false,
  isSomeDropdownOptionsSelected: false,
  isSomeOptionsWithFieldName: false,
  isSelectionsResultExist: false,
  areAllOptionsSelected: false,
  areAllOptionsDisabled: false,
  searchInputValue: '',
  selectionsResult: '',
  currentSelectedOptions: [],
  disabledOptions: [],
  collapsedParentOptions: [],
  toggleDropdown: () => undefined,
  toggleSelectAll: () => undefined,
  toggleParentOptions: () => undefined,
  handleSearchInputValueChange: () => undefined,
  handleSearchInputValueClear: () => undefined,
  handleDropdownItemSelect: () => undefined,
  handleSelectedOptionsClear: () => undefined,
  togglerRef: null,
});

export const useDropdownWithChildrenContext = (): DropdownWithChildrenContextType => {
  return useContext(DropdownWithChildrenContext);
};

const DropdownWithChildrenProvider = ({
  withFormik,
  withSelectionsResult,
  isMultipleSelect,
  isInitialSelectedOptionsArray,
  dropdownTogglerType,
  name,
  maxSelectionsResultLength,
  options,
  formikSelectedOptions = [],
  // Required if not using with formik to see selected options
  initialSelectedOptions = [],
  initialDisabledOptions = [],
  setFieldTouched,
  setFieldValue,
  validateField,
  onOptionsChange,
  children,
}: DropdownWithChildrenProviderType): JSX.Element => {
  const togglerRef = useRef(null);

  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [selectedOptions, setSelectedOptions] = useState(initialSelectedOptions);
  const [collapsedParentOptions, setCollapsedParentOptions] = useState([]);

  const allNestedOptions = useMemo(() => getAllNestedOptions(options), [options]);
  const currentSelectedOptions = useMemo(() => {
    return withFormik ? formikSelectedOptions : selectedOptions;
  }, [withFormik, formikSelectedOptions, selectedOptions]);
  const disabledOptions = useMemo(() => {
    const allNestedOptionsWithZeroCount = _filter(allNestedOptions, { count: 0 });

    return _uniqBy([...initialDisabledOptions, ...allNestedOptionsWithZeroCount], 'value');
  }, [initialDisabledOptions, allNestedOptions]);

  const getEnabledOptions = useCallback(
    (originalOptions: SelectOption[]): SelectOption[] => {
      return _filter(originalOptions, (item) => !_some(disabledOptions, { value: item.value }));
    },
    [disabledOptions]
  );

  const isSomeOptionsWithFieldName = useMemo(() => {
    return _some(options, (option) => option.fieldName);
  }, [options]);
  const areAllOptionsSelected = useMemo(() => {
    const enabledOptions = getEnabledOptions(allNestedOptions);

    return enabledOptions?.length > 0 ? haveSameContents(enabledOptions, currentSelectedOptions) : false;
  }, [currentSelectedOptions, getEnabledOptions]);
  const areAllOptionsDisabled = useMemo(() => {
    return disabledOptions?.length > 0 ? haveSameContents(disabledOptions, allNestedOptions) : false;
  }, [disabledOptions, allNestedOptions]);
  const formikActions = useMemo(
    () => ({
      setFieldTouched,
      setFieldValue,
      validateField,
    }),
    []
  );

  const toggleInputFocus = useCallback((): void => {
    if (!isDropdownOpen) {
      togglerRef.current.focus();
    } else {
      togglerRef.current.blur();

      setSearchInputValue('');
    }
  }, [isDropdownOpen, togglerRef]);

  const toggleDropdown = useCallback((): void => {
    if (dropdownTogglerType === DropdownTogglerTypeEnum.input) {
      toggleInputFocus();
    }

    if (dropdownTogglerType === DropdownTogglerTypeEnum.button) {
      setSearchInputValue('');
    }

    setDropdownOpen((prevDropdownOpen) => !prevDropdownOpen);
    setCollapsedParentOptions([]);
  }, [toggleInputFocus]);

  const toggleSelectAll = useCallback(() => {
    let updatedSelectedOptions;

    if (areAllOptionsSelected) {
      updatedSelectedOptions = [];
    } else {
      const enabledOptions = getEnabledOptions(allNestedOptions);

      updatedSelectedOptions = enabledOptions;
    }

    if (withFormik) {
      setFieldValueAndTouched(name, updatedSelectedOptions, formikActions);
    } else {
      setSelectedOptions(updatedSelectedOptions);
    }

    if (onOptionsChange) {
      onOptionsChange(updatedSelectedOptions);
    }
  }, [areAllOptionsSelected, allNestedOptions, getEnabledOptions]);

  const toggleParentOptions = useCallback(
    (parentItem: SelectOption): void => {
      let updatedCollapsedParentItens;

      if (_some(collapsedParentOptions, { value: parentItem.value })) {
        updatedCollapsedParentItens = _filter(
          collapsedParentOptions,
          (collapsedParentItem) => collapsedParentItem.value !== parentItem.value
        );
      } else {
        updatedCollapsedParentItens = [...collapsedParentOptions, parentItem];
      }

      setCollapsedParentOptions(updatedCollapsedParentItens);
    },
    [collapsedParentOptions]
  );

  const handleSearchInputValueChange = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
    setSearchInputValue(event.currentTarget.value);
  }, []);

  const handleSearchInputValueClear = useCallback(() => {
    setSearchInputValue('');
  }, []);

  const handleDropdownItemSelect = useCallback(
    (newOptions: SelectOption[]): void => {
      let updatedSelectedOptions;

      if (isMultipleSelect) {
        const enabledNewOptions = getEnabledOptions(newOptions);
        const newOptionsValue = _map(enabledNewOptions, 'value');
        const areNewOptionsExist =
          enabledNewOptions?.length > 0
            ? _every(newOptionsValue, (newItemValue) => _includes(_map(currentSelectedOptions, 'value'), newItemValue))
            : _some(currentSelectedOptions, (selectedItem) => _includes(newOptionsValue, selectedItem.value));

        updatedSelectedOptions = areNewOptionsExist
          ? _filter(currentSelectedOptions, (selectedItem) => !_includes(newOptionsValue, selectedItem.value))
          : [...currentSelectedOptions, ...enabledNewOptions];
      } else {
        updatedSelectedOptions = newOptions;
      }

      const newSelectedOptions = isInitialSelectedOptionsArray ? updatedSelectedOptions : updatedSelectedOptions[0];

      if (withFormik) {
        setFieldValueAndTouched(name, newSelectedOptions, formikActions);
      } else {
        setSelectedOptions(newSelectedOptions);
      }

      if (onOptionsChange) {
        onOptionsChange(newSelectedOptions);
      }

      if (!isMultipleSelect) {
        toggleDropdown();
      }
    },
    [isInitialSelectedOptionsArray, currentSelectedOptions, disabledOptions, getEnabledOptions, toggleDropdown]
  );

  const handleSelectedOptionsClear = useCallback(
    (event: MouseEvent<HTMLDivElement>): void => {
      event.stopPropagation();

      const newSelectedOptions = isInitialSelectedOptionsArray ? [] : emptyOption;

      if (withFormik) {
        setFieldValueAndTouched(name, newSelectedOptions, formikActions);
      } else {
        setSelectedOptions(newSelectedOptions);
      }

      if (onOptionsChange) {
        onOptionsChange(newSelectedOptions);
      }
    },
    [isInitialSelectedOptionsArray]
  );

  useEffect(() => {
    const enabledOptions = getEnabledOptions(currentSelectedOptions);

    if (enabledOptions.length > 0 && initialDisabledOptions.length > 0) {
      const newEnabledOptions = isInitialSelectedOptionsArray ? enabledOptions : enabledOptions[0];

      if (withFormik) {
        setFieldValue(name, newEnabledOptions);
      } else {
        setSelectedOptions(newEnabledOptions);
      }
    }
  }, [initialDisabledOptions]);

  useEffect(() => {
    if (!withFormik) {
      setSelectedOptions(initialSelectedOptions);
    }
  }, [initialSelectedOptions]);

  const selectionsResult = useMemo(
    () =>
      withSelectionsResult
        ? getSelectionsResult({ isMultipleSelect, maxSelectionsResultLength, selectedOptions: currentSelectedOptions })
        : '',
    [isMultipleSelect, currentSelectedOptions]
  );

  const isSomeDropdownOptionsSelected = useMemo(
    () => _filter(currentSelectedOptions, (item) => item.value)?.length > 0,
    [currentSelectedOptions]
  );
  const isSelectionsResultExist = useMemo(() => withSelectionsResult && Boolean(selectionsResult), [selectionsResult]);

  const providerValue = useMemo(
    () => ({
      isDropdownOpen,
      isSomeDropdownOptionsSelected,
      isSomeOptionsWithFieldName,
      isSelectionsResultExist,
      areAllOptionsSelected,
      areAllOptionsDisabled,
      searchInputValue,
      selectionsResult,
      currentSelectedOptions,
      disabledOptions,
      collapsedParentOptions,
      toggleDropdown,
      toggleSelectAll,
      toggleParentOptions,
      handleSearchInputValueChange,
      handleSearchInputValueClear,
      handleDropdownItemSelect,
      handleSelectedOptionsClear,
      togglerRef,
    }),
    [
      isDropdownOpen,
      isSomeDropdownOptionsSelected,
      isSomeOptionsWithFieldName,
      isSelectionsResultExist,
      areAllOptionsSelected,
      areAllOptionsDisabled,
      searchInputValue,
      selectionsResult,
      currentSelectedOptions,
      disabledOptions,
      collapsedParentOptions,
      toggleDropdown,
      toggleSelectAll,
      toggleParentOptions,
      handleSearchInputValueChange,
      handleSearchInputValueClear,
      handleDropdownItemSelect,
      handleSelectedOptionsClear,
      togglerRef,
    ]
  );

  return <DropdownWithChildrenContext.Provider value={providerValue}>{children}</DropdownWithChildrenContext.Provider>;
};

export default ({ withFormik, isMultipleSelect, name, initialSelectedOptions, ...restProps }) =>
  withFormik ? (
    <Field name={name}>
      {({ field, form }) => {
        const isInitialSelectedOptionsArray = _isArray(form.initialValues[name]);
        const formikSelectedOptions = useMemo(() => {
          return _isArray(field.value) ? field.value : [field.value || emptyOption];
        }, [field.value]);

        return (
          <DropdownWithChildrenProvider
            withFormik={withFormik}
            isMultipleSelect={isMultipleSelect}
            isInitialSelectedOptionsArray={isInitialSelectedOptionsArray}
            name={name}
            formikSelectedOptions={formikSelectedOptions}
            setFieldTouched={form.setFieldTouched}
            setFieldValue={form.setFieldValue}
            validateField={form.validateField}
            {...restProps}
          />
        );
      }}
    </Field>
  ) : (
    <DropdownWithChildrenProvider
      withFormik={withFormik}
      isMultipleSelect={isMultipleSelect}
      isInitialSelectedOptionsArray={_isArray(initialSelectedOptions)}
      initialSelectedOptions={initialSelectedOptions}
      {...restProps}
    />
  );
